import './styles/DropdownResources.scss';

import { CaretDown, Check } from '@phosphor-icons/react';
import i18n from 'i18next';
import { AnimatePresence, motion } from 'motion/react';
import React, {
  Fragment,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../../helpers/classBuilders';
import { useClassHelper, useOutsideClick } from '../../../../hooks';
import { InputErrorMessage } from '../../../InputErrorMessage';
import { InputField } from '../../../InputField';
import { Loading } from '../../../Loading';
import { Paragraph } from '../../../Typography';

/** CSS class for component (B in BEM) */
const componentName = 'c-DropdownResources';

const VARIANTS = {
  native: 'native',
  minimalist: 'minimalist',
} as const;

type Variant = (typeof VARIANTS)[keyof typeof VARIANTS]; // 'native' | 'minimalist'

type ValueLabelType = { value: string; label: string };

type ResourceType = {
  id: string;
  description: string;
};

/**
 * Props for the "native" variant (select element).
 * We keep most of the Select element attributes but add/override the specific fields used by this component.
 */
export type NativeProps = {
  variant?: typeof VARIANTS.native;
  resources: ResourceType[];
  placeholder?: string;
  withPlaceholder?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  label?: string;
  name?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  className?: string;
  value?: string | number;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'>;

/**
 * Props for the "minimalist" variant (button-like control).
 * Extends Button attributes but forbids the 'value' attribute from ButtonHTMLAttributes,
 * and adds the additional shape the minimalist control needs.
 */
export type MinimalistProps = {
  variant: typeof VARIANTS.minimalist;
  label?: string;
  options: ValueLabelType[];
  id: string;
  placeholder?: string;
  value: ValueLabelType;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  // new required fields for minimalist
  name: string;
  onChange: (
    e: React.ChangeEvent<{ name: string; value: ValueLabelType }>,
  ) => void;
  onBlur: (e: React.FocusEvent<any>) => void;
  hasSearch?: boolean;
  loading?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'>;

/**
 * Discriminated union of the two variants.
 * TypeScript will use the `variant` property to narrow the union.
 */
export type Props = NativeProps | MinimalistProps;

/**
 * Component implementation that treats `native` as the default variant when `variant` is omitted.
 */
export const DropdownResources = (props: Props) => {
  const c = useClassHelper(componentName);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedOption, setFocusedOption] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const optionsRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Need to clear search term when closing the dropdown
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current?.contains(event.target as Node)) {
      return;
    }

    setIsOpen(false);
  };

  /**
   * Handles keydown events to close the popover when the Escape key is pressed.
   */
  useOutsideClick(contentRef, handleClickOutside);

  // Treat undefined variant as native by default
  const variant = (props.variant ?? VARIANTS.native) as Variant;

  if (variant === VARIANTS.native) {
    // Narrow props to NativeProps
    const p = props as NativeProps;

    const {
      error,
      errorMessage,
      resources = [],
      ariaLabel,
      placeholder = 'componentDropdownResources_placeholder',
      withPlaceholder = true,
      loading = false,
      value,
      label,
      name,
      required,
      disabled,
      className,
      // variant omitted on purpose, we're already using computed `variant`
      ...selectProps
    } = p;

    return (
      <div className={c('base')}>
        {label && (
          <InputField.Label
            id={name}
            required={required}
            className="text-gray-600!"
          >
            {label}
          </InputField.Label>
        )}
        <div className={c('__wrapper')}>
          {loading && (
            <div className={c('__loading')} aria-hidden>
              <Loading size={16} />
            </div>
          )}
          {!loading && (
            <span className={c('__caret')} aria-hidden>
              <CaretDown size={18} />
            </span>
          )}
          <select
            className={`${c('__select', {
              '--not-selected': !value,
              '--loading': loading,
            })} ${className || ''} ${cn(
              'border focus-visible:focus-ring-form',
              // The old `--error` modifier had no rule in any stylesheet — the
              // class was dead and errored selects stayed gray. The unlayered
              // SCSS :focus rule (blue) still outranks both utilities, keeping
              // active above error.
              error ? 'border-(--color-input-border-error)' : 'border-gray-300',
            )}`}
            name={name}
            id={name}
            aria-label={ariaLabel}
            disabled={disabled || loading}
            aria-disabled={disabled || loading}
            value={value}
            required={required}
            {...selectProps}
          >
            {withPlaceholder && (
              <option value="" disabled>
                {t(placeholder)}
              </option>
            )}
            {resources.length > 0 &&
              resources.map((item, index) => (
                <option
                  key={`${name}_${index}`}
                  data-testid="select-option"
                  value={item.id}
                >
                  {i18n.exists(item.id) ? t(item.id) : item.description}
                </option>
              ))}
          </select>
        </div>
        {error && (
          <InputErrorMessage
            id={`${name}ErrorMessage`}
            error={error}
            errorMessage={errorMessage}
          />
        )}
      </div>
    );
  }

  if (variant === VARIANTS.minimalist) {
    const p = props as MinimalistProps;
    const {
      label,
      name,
      id,
      options,
      placeholder,
      value,
      required,
      error,
      errorMessage,
      disabled,
      onBlur,
      onChange,
      hasSearch = false,
      loading = false,
      ...selectProps
    } = p;

    const renderSelectedOptions = () => {
      if (!value) return placeholder || 'Select options';
      return value.label;
    };

    const handleBlur = () => {
      const event = {
        target: { name },
      } as React.FocusEvent<any>;
      onBlur(event);
    };

    const filteredOptions = options?.filter(
      option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.value.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    // Handle opening the popover and resetting focus
    const openDropdown = () => {
      setIsOpen(!isOpen);
      setFocusedOption(value ? value.value : filteredOptions[0]?.value || null);
    };

    const handleButtonKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedOption(
          value ? value.value : filteredOptions[0]?.value || null,
        );
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    const handleOptionKeyDown = (
      e: KeyboardEvent<HTMLDivElement>,
      idx: number,
    ) => {
      if (!filteredOptions) {
        return;
      }

      // Navigate options with arrow keys
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx < filteredOptions.length - 1 ? idx + 1 : 0;
        setFocusedOption(filteredOptions[next].value);
        optionsRefs.current[next]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = idx > 0 ? idx - 1 : filteredOptions.length - 1;
        setFocusedOption(filteredOptions[prev].value);
        optionsRefs.current[prev]?.focus();
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        onChange({
          target: { name, value: filteredOptions[idx] },
        } as React.ChangeEvent<{ name: string; value: ValueLabelType }>);
        setIsOpen(false);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        dropdownRef.current?.focus();
      }
    };

    const handleOptionClick = (idx: number) => {
      onChange({
        target: { name, value: filteredOptions[idx] },
      } as React.ChangeEvent<{ name: string; value: ValueLabelType }>);
      setIsOpen(false);
      dropdownRef.current?.focus();
    };

    return (
      <div id={`form-field-${id}`} className="relative">
        <div className="w-full text-left">
          {label && (
            <InputField.Label
              id="dropdown"
              htmlFor="dropdown"
              required={required}
            >
              {label}
            </InputField.Label>
          )}
          <div className="hs-select relative">
            <button
              ref={dropdownRef}
              type="button"
              className={cn(
                'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-0 ps-4 pe-9 flex gap-x-2 text-nowrap w-full cursor-pointer bg-white border border-gray-300 rounded-lg text-start h-10 items-center disabled:opacity-50 disabled:cursor-default disabled:bg-[var(--color-input-background-disabled)]',
                {
                  'border-[var(--color-input-border-error)]': error && !isOpen,
                  // This used to point at the *default* token with a stray `)`
                  // that made the class invalid — the open state was a no-op
                  // and only the :focus rule drew a border.
                  'border-blue-700': isOpen,
                },
              )}
              aria-expanded={isOpen ? 'true' : 'false'}
              id={id}
              onBlur={handleBlur}
              name={name}
              tabIndex={0}
              onClick={openDropdown}
              onKeyDown={handleButtonKeyDown}
              disabled={disabled}
              aria-disabled={disabled}
              aria-haspopup="listbox"
              {...selectProps}
            >
              <span
                className={cn(
                  'truncate text-base text-[var(--color-font-primary)]',
                  {
                    'text-gray-500': !value,
                  },
                )}
              >
                {renderSelectedOptions()}
              </span>
            </button>
            {loading ? (
              <div className="absolute right-[15px] top-1/2 -translate-y-1/2">
                <Loading size={12} />
              </div>
            ) : (
              <div
                className={cn('absolute top-1/2 end-3 -translate-y-1/2', {
                  'text-gray-400': disabled,
                })}
              >
                <CaretDown size={18} aria-hidden="true" />
              </div>
            )}
          </div>
          <InputField.ErrorMessage
            id={id}
            error={error}
            errorMessage={errorMessage}
          />
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1, transition: { duration: 0.1 } }}
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
              ref={contentRef}
              id={id}
              className={cn(
                'absolute mt-2 max-h-72 p-1 space-y-0.5 z-20 w-full bg-white border border-gray-300 rounded-lg overflow-hidden overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 top-full opened',
                {
                  'pt-0': hasSearch,
                },
              )}
              role="listbox"
              aria-orientation="vertical"
              aria-labelledby={`${id}-header`}
              aria-describedby={`${id}-body`}
              tabIndex={-1}
            >
              {hasSearch && (
                <div className="bg-white p-2 -mx-1 sticky top-0">
                  <InputField
                    id="dropdown-search"
                    type="search"
                    placeholder="Search..."
                    name="search"
                    onChange={e => setSearchTerm(e.target.value)}
                    value={searchTerm}
                    onClear={() => setSearchTerm('')}
                    autoFocus
                  />
                </div>
              )}
              {filteredOptions.length > 0 ? (
                <Fragment>
                  {filteredOptions.map((option, idx) => {
                    const isSelected = value?.value === option.value;
                    const isFocused = focusedOption === option.value;
                    return (
                      <div
                        key={option.value}
                        ref={el => {
                          optionsRefs.current[idx] = el;
                        }}
                        className={cn(
                          'cursor-pointer py-2 px-4 w-full text-sm text-gray-800 hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100',
                          {
                            'bg-gray-100': isSelected || isFocused,
                          },
                        )}
                        role="option"
                        aria-selected={isSelected}
                        tabIndex={0}
                        onClick={() => handleOptionClick(idx)}
                        onKeyDown={e => handleOptionKeyDown(e, idx)}
                        onMouseEnter={() => setFocusedOption(option.value)}
                      >
                        <Paragraph tag="span" size="md" isBolder={isSelected}>
                          {option.label}
                        </Paragraph>
                        {isSelected && (
                          <span className="float-end text-blue-700">
                            <Check size={18} />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </Fragment>
              ) : (
                <div className="py-2 px-4 text-sm text-gray-500 text-center">
                  No results found
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return null;
};
